import { Component, effect, input } from '@angular/core';
import {
  MaintananceLevelModel,
  MaintenanceCriteriaFullModel,
  MaintenanceCriteriaModel,
  MaintenanceCriteriaSettingModel,
  PatientTargetStatus,
} from '../../core/api/models';
import { SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SettingsHelperService } from '../settings.helper.service';
import { Utils } from '../../core/utils';
import { forkJoin } from 'rxjs';

interface MaintenanceTransitionOption {
  label: string;
  value: string;
  maintenanceLevelName?: string;
  targetStatusID?: number;
}

interface InitialTransitionOption {
  label: string;
  value: number | null;
}

@Component({
  selector: 'app-maintanance-criterias',
  templateUrl: './maintanance-criterias.component.html',
  styleUrl: './maintanance-criterias.component.css',
  standalone: false,
})
export class MaintananceCriteriasComponent {
  private readonly criteriaLevelOrder = ['Daily', 'Weekly', 'Monthly'];

  private readonly promotionTransitionConfigs: MaintenanceTransitionOption[] = [
    {
      label: 'Daily',
      value: 'maintenance:Daily',
      maintenanceLevelName: 'Daily',
    },
    {
      label: 'Weekly',
      value: 'maintenance:Weekly',
      maintenanceLevelName: 'Weekly',
    },
    {
      label: 'Monthly',
      value: 'maintenance:Monthly',
      maintenanceLevelName: 'Monthly',
    },
    {
      label: 'Fluent',
      value: 'status:Fluent',
      targetStatusID: PatientTargetStatus.Fluent,
    },
  ];

  private readonly demotionTransitionConfigs: MaintenanceTransitionOption[] = [
    {
      label: 'Acquisition',
      value: 'status:Acquisition',
      targetStatusID: PatientTargetStatus.Aquisition,
    },
    {
      label: 'Daily',
      value: 'maintenance:Daily',
      maintenanceLevelName: 'Daily',
    },
    {
      label: 'Weekly',
      value: 'maintenance:Weekly',
      maintenanceLevelName: 'Weekly',
    },
  ];

  patientID = input<string | undefined>();

  maintenanceCriterias: MaintenanceCriteriaModel[] = [];
  maintananceCriteriaSettings: MaintenanceCriteriaSettingModel = {};
  maintenanceLevels: MaintananceLevelModel[] = [];
  legacyMaintananceCriterias: MaintenanceCriteriaModel[] = [];
  legacyMaintananceCriteriaSettings: MaintenanceCriteriaSettingModel | null =
    null;
  showLegacyConfigurationDetails: boolean = false;
  isUsingLegacyMaintenanceCriteria: boolean = false;

  loadingMaintananceCriteria: boolean = false;
  hasCustomMaintananceCriteria: boolean = false;
  isScreenForSinglePatient: boolean = false;
  shouldShowResetToGeneralSettings: boolean = false;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    protected settingsHelperService: SettingsHelperService
  ) {
    effect(() => {
      console.log(
        'update signal triggered: ',
        this.settingsHelperService.updateSignal()
      );
      this.loadData();
    });
  }

  ngOnInit(): void {
    console.log('MaintananceCriteriaComponent ngOnInit');
    this.loadData();
  }

  loadData() {
    this.loadingMaintananceCriteria = true;
    const patinetIdToUse =
      this.patientID() && !isNaN(Number(this.patientID()))
        ? Number(this.patientID())
        : undefined;
    if (patinetIdToUse) {
      this.isScreenForSinglePatient = true;
      this.hasCustomMaintananceCriteria = false;
    }
    const GetMaintenanceCriteriaFullObservable =
      this.supervisorApiService.GetMaintenanceCriteriaFull({
        patientID: patinetIdToUse,
      });

    const GetMaintenanceLevelsObservable =
      this.supervisorApiService.GetMaintananceLevels();

    forkJoin([
      GetMaintenanceCriteriaFullObservable,
      GetMaintenanceLevelsObservable,
    ]).subscribe(
      ([maintenanceCriteriaFull, maintenanceLevels]) => {
        const allMaintenanceCriterias =
          maintenanceCriteriaFull.MaintenanceCriterias ?? [];
        const legacyMaintenanceCriterias = allMaintenanceCriterias.filter(
          (criteria) =>
            !!criteria.IsLegacyMaintenanceCriteria &&
            criteria.MaintenanceLevelID != null
        );
        const activeMaintenanceCriterias = this.normalizeMaintenanceCriterias(
          allMaintenanceCriterias.filter(
            (criteria) => !criteria.IsLegacyMaintenanceCriteria
          )
        );
        this.isUsingLegacyMaintenanceCriteria =
          this.isScreenForSinglePatient &&
          legacyMaintenanceCriterias.length > 0 &&
          activeMaintenanceCriterias.length === 0;

        if (maintenanceCriteriaFull.MaintenanceCriteriaSetting) {
          this.hasCustomMaintananceCriteria = true;
          this.shouldShowResetToGeneralSettings = true;
        }
        this.maintenanceLevels = maintenanceLevels;
        this.maintenanceLevels.push({
          ID: null,
          MaintenanceLevel: 'No change',
        });
        this.legacyMaintananceCriterias = this.isUsingLegacyMaintenanceCriteria
          ? legacyMaintenanceCriterias
          : [];
        this.legacyMaintananceCriteriaSettings = this.isUsingLegacyMaintenanceCriteria
          ? {
              ...(maintenanceCriteriaFull.MaintenanceCriteriaSetting ?? {}),
            }
          : null;
        this.maintenanceCriterias = this.getVisibleMaintenanceCriterias(
          activeMaintenanceCriterias
        );
        for (const maintananceLevel of this.getCriteriaLevels()) {
          if (
            maintananceLevel.ID &&
            !this.maintenanceCriterias.find(
              (x) => x.MaintenanceLevelID === maintananceLevel.ID
            )
          ) {
            this.maintenanceCriterias.push({
              MaintenanceLevelID: maintananceLevel.ID,
              IsActive: true,
              PatientID: patinetIdToUse,
            });
          }
        }
        this.maintananceCriteriaSettings =
          maintenanceCriteriaFull.MaintenanceCriteriaSetting ?? {
            PatientID: patinetIdToUse,
          };

        const shouldLoadGeneralDefaults =
          patinetIdToUse &&
          (!this.hasCustomMaintananceCriteria ||
            (this.hasLegacyMaintenanceCriteria &&
              activeMaintenanceCriterias.length === 0));

        if (shouldLoadGeneralDefaults) {
          this.loadGeneralSettingsAsDefault(patinetIdToUse);

          if (!this.hasLegacyMaintenanceCriteria) {
            this.shouldShowResetToGeneralSettings = false;
          }
        }

        this.loadingMaintananceCriteria = false;
      },
      (err) => {
        this.loadingMaintananceCriteria = false;
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    );
  }

  getMaintananceLevelName(maintenanceLevelID: number | undefined) {
    if (!maintenanceLevelID) {
      return '';
    }
    return this.maintenanceLevels.find((x) => x.ID === maintenanceLevelID)
      ?.MaintenanceLevel;
  }

  get displayMaintenanceCriterias(): MaintenanceCriteriaModel[] {
    const levelOrder = new Map(
      this.criteriaLevelOrder.map((levelName, index) => [levelName, index])
    );

    return [...this.maintenanceCriterias].sort((a, b) => {
      const aName = this.getMaintananceLevelName(a.MaintenanceLevelID) ?? '';
      const bName = this.getMaintananceLevelName(b.MaintenanceLevelID) ?? '';
      return (levelOrder.get(aName) ?? Number.MAX_SAFE_INTEGER) -
        (levelOrder.get(bName) ?? Number.MAX_SAFE_INTEGER);
    });
  }

  get hasLegacyMaintenanceCriteria(): boolean {
    return this.isUsingLegacyMaintenanceCriteria;
  }

  get displayLegacyMaintenanceCriterias(): MaintenanceCriteriaModel[] {
    const levelOrder = new Map(
      this.criteriaLevelOrder.map((levelName, index) => [levelName, index])
    );

    return [...this.legacyMaintananceCriterias].sort((a, b) => {
      const aName = this.getMaintananceLevelName(a.MaintenanceLevelID) ?? '';
      const bName = this.getMaintananceLevelName(b.MaintenanceLevelID) ?? '';
      return (
        (levelOrder.get(aName) ?? Number.MAX_SAFE_INTEGER) -
        (levelOrder.get(bName) ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }

  getLegacyCriteriaTrackKey(
    index: number,
    criteria: MaintenanceCriteriaModel
  ): string {
    return `legacy-${criteria.ID ?? criteria.MaintenanceLevelID ?? index}`;
  }

  get initialTransitionOptions(): InitialTransitionOption[] {
    const maintenanceLevelOptions = this.maintenanceLevels
      .filter(
        (level) =>
          level.ID != null &&
          this.criteriaLevelOrder.includes(level.MaintenanceLevel ?? '')
      )
      .map((level) => ({
        label: this.getInitialTransitionLabel(level.MaintenanceLevel ?? ''),
        value: level.ID ?? null,
      }));

    return [{ label: 'Close Target', value: null }, ...maintenanceLevelOptions];
  }

  get promotionTransitionOptions(): Array<Pick<MaintenanceTransitionOption, 'label' | 'value'>> {
    return this.promotionTransitionConfigs.map(({ label, value }) => ({
      label,
      value,
    }));
  }

  getPromotionTransitionOptions(
    criteria: MaintenanceCriteriaModel
  ): Array<Pick<MaintenanceTransitionOption, 'label' | 'value'>> {
    const maintenanceLevelName =
      this.getMaintananceLevelName(criteria.MaintenanceLevelID) ?? '';
    const keepOption = this.getKeepTransitionOption(maintenanceLevelName);

    if (maintenanceLevelName === 'Monthly') {
      return [
        keepOption,
        ...this.promotionTransitionOptions.filter(
          (option) => option.value === 'status:Fluent'
        ),
      ];
    }

    if (maintenanceLevelName === 'Weekly') {
      return [
        keepOption,
        ...this.promotionTransitionOptions.filter(
          (option) =>
            option.value === 'maintenance:Monthly' ||
            option.value === 'status:Fluent'
        ),
      ];
    }

    return [
      keepOption,
      ...this.promotionTransitionOptions.filter(
        (option) => option.value !== 'maintenance:Daily'
      ),
    ];
  }

  get demotionTransitionOptions(): Array<Pick<MaintenanceTransitionOption, 'label' | 'value'>> {
    return this.demotionTransitionConfigs.map(({ label, value }) => ({
      label,
      value,
    }));
  }

  getDemotionTransitionOptions(
    criteria: MaintenanceCriteriaModel
  ): Array<Pick<MaintenanceTransitionOption, 'label' | 'value'>> {
    const maintenanceLevelName =
      this.getMaintananceLevelName(criteria.MaintenanceLevelID) ?? '';
    const keepOption = this.getKeepTransitionOption(maintenanceLevelName);

    if (maintenanceLevelName === 'Daily') {
      return [
        keepOption,
        ...this.demotionTransitionOptions.filter(
          (option) => option.value === 'status:Acquisition'
        ),
      ];
    }

    if (maintenanceLevelName === 'Weekly') {
      return [
        keepOption,
        ...this.demotionTransitionOptions.filter(
          (option) =>
            option.value === 'maintenance:Daily' ||
            option.value === 'status:Acquisition'
        ),
      ];
    }

    return [
      keepOption,
      ...this.demotionTransitionOptions,
    ];
  }

  getPromotionTransitionValue(criteria: MaintenanceCriteriaModel): string | null {
    return this.getTransitionValue(criteria, this.promotionTransitionConfigs, true);
  }

  getDemotionTransitionValue(criteria: MaintenanceCriteriaModel): string | null {
    return this.getTransitionValue(criteria, this.demotionTransitionConfigs, false);
  }

  updatePromotionTransition(
    criteria: MaintenanceCriteriaModel,
    value: string | null
  ): void {
    this.applyTransitionValue(criteria, value, this.promotionTransitionConfigs, true);
  }

  updateDemotionTransition(
    criteria: MaintenanceCriteriaModel,
    value: string | null
  ): void {
    this.applyTransitionValue(criteria, value, this.demotionTransitionConfigs, false);
  }

  toggleLegacyConfigurationDetails(): void {
    this.showLegacyConfigurationDetails = !this.showLegacyConfigurationDetails;
  }

  isCriteriaReadOnly(criteria: MaintenanceCriteriaModel): boolean {
    return !this.hasCustomMaintananceCriteria && this.isScreenForSinglePatient;
  }

  save() {
    this.loadingMaintananceCriteria = true;
    const maintananceCriteriaSettingsToUse: MaintenanceCriteriaFullModel = {
      PatientID:
        this.patientID() && !isNaN(Number(this.patientID()))
          ? Number(this.patientID())
          : undefined,
      MaintenanceCriteriaSetting: this.maintananceCriteriaSettings,
      MaintenanceCriterias: this.displayMaintenanceCriterias,
    };
    this.supervisorApiService
      .SaveMaintenanceCriteriaFull({ body: maintananceCriteriaSettingsToUse })
      .subscribe(
        () => {
          this.loadingMaintananceCriteria = false;
          this.hasCustomMaintananceCriteria = true;
          this.shouldShowResetToGeneralSettings = true;
          this.alertService.success('Maintenance criteria saved successfully');
          this.loadData();
        },
        (err) => {
          this.loadingMaintananceCriteria = false;
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        }
      );
  }

  enableCustomMaintananceCriteria() {
    this.save();
  }

  loadGeneralSettingsAsDefault(patientID: number | undefined): void {
    forkJoin([
      this.supervisorApiService.GetMaintenanceCriteriaFull({
        patientID: undefined
      }),
      this.supervisorApiService.GetMaintananceLevels()
    ]).subscribe({
      next: ([generalMaintenanceCriteriaFull, maintenanceLevels]) => {
        if (generalMaintenanceCriteriaFull.MaintenanceCriteriaSetting) {
          this.maintananceCriteriaSettings = {
            ...generalMaintenanceCriteriaFull.MaintenanceCriteriaSetting,
            PatientID: patientID,
            ID: undefined // Clear ID so it's treated as new custom settings when saved
          };
        }
        
        if (generalMaintenanceCriteriaFull.MaintenanceCriterias && generalMaintenanceCriteriaFull.MaintenanceCriterias.length > 0) {
          // Map general maintenance criterias to match the structure
          const generalCriterias = this.normalizeMaintenanceCriterias(
            generalMaintenanceCriteriaFull.MaintenanceCriterias
          );
          this.maintenanceCriterias = this.maintenanceCriterias.map(criteria => {
            const generalCriteria = generalCriterias.find(gc => gc.MaintenanceLevelID === criteria.MaintenanceLevelID);
            if (generalCriteria) {
              return {
                ...generalCriteria,
                PatientID: patientID,
                ID: undefined // Clear ID so it's treated as new custom settings when saved
              };
            }
            return criteria;
          });
        }
      },
      error: (err) => {
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });
  }

  normalizeMaintenanceCriterias(
    criterias: MaintenanceCriteriaModel[]
  ): MaintenanceCriteriaModel[] {
    const criteriasByLevel = new Map<number, MaintenanceCriteriaModel>();

    for (const criteria of criterias) {
      if (!criteria.MaintenanceLevelID) {
        continue;
      }

      const existingCriteria = criteriasByLevel.get(criteria.MaintenanceLevelID);
      if (!existingCriteria) {
        criteriasByLevel.set(criteria.MaintenanceLevelID, criteria);
        continue;
      }

      const existingId = existingCriteria.ID ?? Number.MAX_SAFE_INTEGER;
      const currentId = criteria.ID ?? Number.MAX_SAFE_INTEGER;
      if (currentId < existingId) {
        criteriasByLevel.set(criteria.MaintenanceLevelID, criteria);
      }
    }

    return Array.from(criteriasByLevel.values());
  }

  resetToGeneralSettings() {
    this.supervisorApiService.RemoveCustomMaintenanceCriteria({
      body: {
        PatientID: this.maintananceCriteriaSettings.PatientID,
      },
    }).subscribe({
      next: () => {
        this.alertService.success('Maintenance criteria reset to general settings successfully');
        this.loadData();
      },
      error: (err) => {
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });
  }

  get pageModeLabel(): string {
    if (!this.isScreenForSinglePatient) {
      return 'General Maintenance Criteria';
    }

    if (this.hasLegacyMaintenanceCriteria) {
      return 'Legacy Patient-Specific Maintenance Criteria';
    }

    return this.hasCustomMaintananceCriteria
      ? 'Patient-Specific Maintenance Criteria'
      : 'Using General Maintenance Criteria';
  }

  get pageSummary(): string {
    if (!this.isScreenForSinglePatient) {
      return 'These maintenance criteria are used when no patient-specific criteria exist.';
    }

    if (this.hasLegacyMaintenanceCriteria) {
      return 'This patient has legacy maintenance criteria. Review the previous configuration and save a new patient-specific maintenance criteria using the updated three-level flow.';
    }

    if (this.hasCustomMaintananceCriteria) {
      return 'These patient-specific maintenance criteria override the general defaults for this patient.';
    }

    return 'This patient is currently inheriting the general maintenance criteria settings.';
  }

  get probeLevelHint(): string {
    return 'Used when a target reaches maintenance from Baseline mastery.';
  }

  get acquisitionLevelHint(): string {
    return 'Used when a target reaches maintenance from Treatment mastery.';
  }

  get levelRulesHint(): string {
    return 'Each level defines how many successes or misses are needed before Spark promotes or demotes a target, and where the target moves next.';
  }

  getLegacyInitialTransitionLabel(
    maintenanceLevelID: number | null | undefined
  ): string {
    if (maintenanceLevelID == null) {
      return 'Close Target';
    }

    const maintenanceLevelName =
      this.getMaintananceLevelName(maintenanceLevelID) ?? `Level #${maintenanceLevelID}`;
    return this.getInitialTransitionLabel(maintenanceLevelName);
  }

  getLegacyCriteriaLevelLabel(criteria: MaintenanceCriteriaModel): string {
    return (
      this.getMaintananceLevelName(criteria.MaintenanceLevelID) ??
      `Level #${criteria.MaintenanceLevelID}`
    );
  }

  getLegacyTransitionStatusLabel(
    criteria: MaintenanceCriteriaModel,
    isPromotion: boolean
  ): string {
    const targetStatusID = isPromotion
      ? criteria.TargetStatusIDWhenPromoting
      : criteria.TargetStatusIDWhenDemoting;

    if (targetStatusID != null) {
      return this.getTargetStatusLabel(targetStatusID);
    }

    return 'No change';
  }

  getLegacyTransitionMaintenanceLevelLabel(
    criteria: MaintenanceCriteriaModel,
    isPromotion: boolean
  ): string {
    const maintenanceLevelID = isPromotion
      ? criteria.MaintenanceLevelIDWhenPromoting
      : criteria.MaintenanceLevelIDWhenDemoting;

    if (maintenanceLevelID != null) {
      const maintenanceLevelName =
        this.getMaintananceLevelName(maintenanceLevelID) ??
        `Level #${maintenanceLevelID}`;
      return this.getInitialTransitionLabel(maintenanceLevelName);
    }

    return 'No change';
  }

  formatThreshold(
    thresholdNeeded: number | null | undefined,
    thresholdTotal: number | null | undefined
  ): string {
    return `${thresholdNeeded ?? '-'} out of ${thresholdTotal ?? '-'}`;
  }

  private getTransitionValue(
    criteria: MaintenanceCriteriaModel,
    options: MaintenanceTransitionOption[],
    isPromotion: boolean
  ): string | null {
    const targetStatusID = isPromotion
      ? criteria.TargetStatusIDWhenPromoting
      : criteria.TargetStatusIDWhenDemoting;
    const maintenanceLevelID = isPromotion
      ? criteria.MaintenanceLevelIDWhenPromoting
      : criteria.MaintenanceLevelIDWhenDemoting;

    if (targetStatusID != null) {
      return (
        options.find((option) => option.targetStatusID === targetStatusID)?.value ??
        null
      );
    }

    if (maintenanceLevelID != null) {
      const maintenanceLevelName =
        this.getMaintananceLevelName(maintenanceLevelID) ?? '';
      return (
        options.find(
          (option) => option.maintenanceLevelName === maintenanceLevelName
        )?.value ?? null
      );
    }

    return null;
  }

  private applyTransitionValue(
    criteria: MaintenanceCriteriaModel,
    value: string | null,
    options: MaintenanceTransitionOption[],
    isPromotion: boolean
  ): void {
    const selectedOption =
      options.find((option) => option.value === value) ?? null;
    const maintenanceLevelID = selectedOption?.maintenanceLevelName
      ? this.getMaintenanceLevelId(selectedOption.maintenanceLevelName)
      : null;
    const targetStatusID = selectedOption?.targetStatusID ?? null;

    if (isPromotion) {
      criteria.MaintenanceLevelIDWhenPromoting = maintenanceLevelID;
      criteria.TargetStatusIDWhenPromoting = targetStatusID;
      return;
    }

    criteria.MaintenanceLevelIDWhenDemoting = maintenanceLevelID;
    criteria.TargetStatusIDWhenDemoting = targetStatusID;
  }

  private getMaintenanceLevelId(maintenanceLevelName: string): number | null {
    return (
      this.maintenanceLevels.find(
        (level) => level.MaintenanceLevel === maintenanceLevelName
      )?.ID ?? null
    );
  }

  private getCriteriaLevels(): MaintananceLevelModel[] {
    return this.maintenanceLevels.filter(
      (level) =>
        level.ID != null &&
        this.criteriaLevelOrder.includes(level.MaintenanceLevel ?? '')
    );
  }

  private getVisibleMaintenanceCriterias(
    criterias: MaintenanceCriteriaModel[]
  ): MaintenanceCriteriaModel[] {
    const visibleLevelIds = new Set(
      this.getCriteriaLevels()
        .map((level) => level.ID)
        .filter((id): id is number => id != null)
    );

    return criterias.filter(
      (criteria) =>
        criteria.MaintenanceLevelID != null &&
        visibleLevelIds.has(criteria.MaintenanceLevelID)
    );
  }

  private getInitialTransitionLabel(maintenanceLevelName: string): string {
    return maintenanceLevelName;
  }

  private getTargetStatusLabel(targetStatusID: number): string {
    if (targetStatusID === PatientTargetStatus.Aquisition) {
      return 'Acquisition';
    }

    if (targetStatusID === PatientTargetStatus.Fluent) {
      return 'Fluent';
    }

    return `Status #${targetStatusID}`;
  }

  private getKeepTransitionOption(
    maintenanceLevelName: string
  ): Pick<MaintenanceTransitionOption, 'label' | 'value'> {
    return {
      label: `${maintenanceLevelName} (Keep)`,
      value: `maintenance:${maintenanceLevelName}`,
    };
  }
}
